import { ArchiveProjectDialog } from "@/components/estimation-details/ArchiveProjectDialog";
import { ChangeDefaultNameAlert } from "@/components/estimation-details/ChangeDefaultNameAlert";
import { Cat3LineHighlightProvider } from "@/components/estimation-details/cat3-sync/Cat3LineHighlightContext";
import DetailsSection from "@/components/estimation-details/details-section/DetailsSection";
import { RoomsSection } from "@/components/estimation-details/RoomsSection";
import FixMissingMappingModal from "@/components/estimation-details/xactimate/FixMissingMappingModal";
import FixMissingDimensionsModal from "@/components/estimation-details/FixMissingDimensionsModal";
import PDFPreviewModal from "@/components/estimation-details/pdf-preview/PDFPreviewModal";
import PhotoReportExportModal from "@/components/estimation-details/pdf-preview/photo-report/PhotoReportExportModal";
import { ToggleShowDebugItems } from "@/components/estimation-details/Rooms/ToggleShowDebugItems";
import { Header } from "@/components/layout/Header";
import { WarningAlert } from "@/components/modals/WarningAlert";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useArchiveEstimateMutation } from "@/hooks/mutation/estimates/useArchiveEstimateMutation";
import { useExportToXactimateMutation } from "@/hooks/mutation/estimates/useExportToXactimateMutation";
import { useValidatePDFExportMutation } from "@/hooks/mutation/pdf-export";
import { useAnalytics } from "@/analytics";
import { useValidateXactimateExportMutation } from "@/hooks/mutation/estimates/useValidateXactimateExportMutation";
import type { Estimation } from "@/interfaces/estimation.interface";
import type { MissingDimensionInfo, DimensionInfo } from "@rebuild/shared";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { useDispatch } from "@/redux/store";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";

import { EstimateHeaderActions } from "@/components/estimation-details/EstimateHeaderActions";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { MobileEstimationDetail } from "@/components/estimation-details/MobileEstimationDetail";
import { AddDocumentationDialog } from "@/components/estimation-details/upload/AddDocumentationDialog";
import { EstimateType } from "@/enums/estimate-type";
import { v4 as uuidv4 } from "uuid";
import { EstimateStatus, isItemArchived } from "@rebuild/shared";
import { toast } from "sonner";
import { generateProjectName } from "@/components/layout/Layout";
import estimateProvider from "@/providers/estimate.provider";
import { useIsMobile } from "@/hooks/useIsMobile";
import { clearClipboard } from "@/components/estimation-details/Rooms/hooks/useCopyPaste";
import { getEstimateDetailBackTarget } from "./navigation";
import { Button } from "@/components/ui/button";

type PDFExportStep =
  | { type: "idle" }
  | {
      type: "missing_dimensions";
      missingDimensions: MissingDimensionInfo[];
      isTimeAndMaterial: boolean;
    }
  | {
      type: "preview";
      collectedDimensions: DimensionInfo[];
      isTimeAndMaterial: boolean;
    }
  | { type: "photo_report" };

export const EstimationDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { estimateId } = useParams();

  const { isFeatureEnabled } = useFeatureFlags();

  const [showUpdateName, setShowUpdateName] = useState(false);
  const [showAddDocDialog, setShowAddDocDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const { mutateAsync: archiveEstimate, isPending: isArchiving } = useArchiveEstimateMutation();

  const {
    alert,
    estimation,
    onCloseAlert,
    lineItemDetail,
    deleteLineItem,
    estimationDetail,
    searchEstimateDetail,
    fixedMapping,
    onCancelMissingMapping,
    showMissingMappingModal,
    resetEstimationDetailData,
    exportedToXactimate,
    hasPdfExport,
    isUpdateScopeNoteLoading,
    isUpdateLineItemLoading,
    refetchEstimation,
    estimateNotFound,
  } = useEstimateDetail(estimateId!, { pollJobs: true });

  const hasLineItems = useMemo(
    () =>
      (estimationDetail ?? []).some((room) =>
        room.dataForUI?.page?.tuplesOutput?.some(
          (tuple) => !isItemArchived(tuple) && tuple.lineItems.some((lineItem) => !isItemArchived(lineItem)),
        ),
      ),
    [estimationDetail],
  );

  // When navigating from the dashboard, the full estimate is passed in router state (the same
  // object the dashboard list already holds). Seed it into redux synchronously before paint so
  // the detail view renders immediately with the estimate's data instead of flashing a skeleton
  // while the metadata refetches in the background. Deep links / refreshes (no state) fall back
  // to the normal fetch.
  const seededEstimate = (location.state as { estimate?: Estimation } | null)?.estimate;
  useLayoutEffect(() => {
    if (seededEstimate && estimation === undefined) {
      dispatch(
        estimationActions.setEstimationState({
          estimation: seededEstimate,
          links: seededEstimate.links ?? [],
          notes: seededEstimate.notes ?? [],
          files: seededEstimate.files ?? [],
        }),
      );
    }
    // Seed once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: validateXactimateExport } = useValidateXactimateExportMutation();
  const { mutateAsync: exportToXactimate, isPending: isExporting } = useExportToXactimateMutation({
    onExportSuccess: () => {
      dispatch(
        estimationActions.setEstimationState({
          exportedToXactimate: true,
          missingMappings: [],
          fixedMapping: [],
        }),
      );
    },
  });

  const [exportAlert, setExportAlert] = useState<{ title?: string; errors: string[]; isValid: boolean }>({
    isValid: true,
    errors: [],
  });
  const [isPreparingXactimateExport, setIsPreparingXactimateExport] = useState(false);

  const combinedAlert = useMemo(() => {
    if (!exportAlert.isValid) return exportAlert;
    return alert;
  }, [alert, exportAlert]);

  const handleCloseAlert = useCallback(() => {
    setExportAlert({ errors: [], isValid: true });
    onCloseAlert();
  }, [onCloseAlert]);

  const handleExportToXactimate = useCallback(async () => {
    if (!estimateId) return;

    setIsPreparingXactimateExport(true);
    try {
      const validation = await validateXactimateExport(estimateId);

      if (validation.missingFields.length > 0) {
        dispatch(
          estimationActions.setEstimationState({
            rightSidebarCurrentTab: { id: "Details", value: "Details" },
            lineItemDetail: undefined,
          }),
        );
        setExportAlert({
          isValid: false,
          title: "The following fields are required to export to Xactimate:",
          errors: validation.missingFields,
        });
        return;
      }

      if (validation.missingMappings.length > 0) {
        dispatch(
          estimationActions.setEstimationState({
            showMissingMappingModal: true,
            missingMappings: validation.missingMappings,
          }),
        );
        return;
      }

      const result = await exportToXactimate({
        estimateId,
        fixedMapping: fixedMapping ?? undefined,
      });

      if (result.status === "exported") {
        refetchEstimation();
      }
    } finally {
      setIsPreparingXactimateExport(false);
    }
  }, [estimateId, fixedMapping, validateXactimateExport, exportToXactimate, dispatch, refetchEstimation]);

  const handleContinueExporting = useCallback(async () => {
    if (!estimateId) return;

    dispatch(
      estimationActions.setEstimationState({
        missingMappings: [],
        showMissingMappingModal: false,
      }),
    );

    // Export directly using the resolved/discarded mappings the user confirmed.
    const result = await exportToXactimate({
      estimateId,
      fixedMapping: fixedMapping ?? undefined,
    });

    if (result.status === "exported") {
      refetchEstimation();
    }
  }, [estimateId, fixedMapping, exportToXactimate, dispatch, refetchEstimation]);

  const canContinueExporting = useMemo(() => {
    return fixedMapping?.every(
      (mapping) =>
        mapping.discard || (mapping.cat?.length && mapping.activity?.length && mapping.quantity?.length && mapping.variable?.length),
    );
  }, [fixedMapping]);

  const { mutateAsync: validatePDFExport, isPending: isValidating } = useValidatePDFExportMutation();
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();
  const [exportStep, setExportStep] = useState<PDFExportStep>({ type: "idle" });
  const [isVerbose, setIsVerbose] = useState(false);

  const isTimeAndMaterial = estimation?.estimateType === EstimateType.TIME_AND_MATERIAL;

  const handlePDFExport = useCallback(
    async (verbose: boolean = false) => {
      if (!estimateId) return;

      trackEvent(ANALYTICS_EVENTS.PDF_EXPORT_INITIATED, {
        estimate_id: estimateId,
        export_type: verbose ? "itemized" : "summarized",
      });

      setIsVerbose(verbose);

      try {
        const validationResult = await validatePDFExport(estimateId);

        if (validationResult.requiresOfficeSelection) {
          dispatch(
            estimationActions.setEstimationState({
              rightSidebarCurrentTab: { id: "Details", value: "Details" },
              lineItemDetail: undefined,
            }),
          );
          setExportAlert({
            isValid: false,
            title: "The following fields are required to export to PDF:",
            errors: ["Office"],
          });
          return;
        }

        const hasMissingDimensions = validationResult.dimension.length > 0;

        if (hasMissingDimensions) {
          setExportStep({
            type: "missing_dimensions",
            missingDimensions: validationResult.dimension,
            isTimeAndMaterial,
          });
          return;
        }

        // No missing data, go straight to preview
        setExportStep({
          type: "preview",
          collectedDimensions: [],
          isTimeAndMaterial,
        });
      } catch {
        toast.error("Failed to validate PDF export", {
          className: "!text-red-500 !bg-red-500/10 !border-red-500",
        });
      }
    },
    [dispatch, estimateId, estimation?.estimateType, validatePDFExport, trackEvent, ANALYTICS_EVENTS],
  );

  const handleContinueWithDimensions = useCallback(
    (dimensionsData: DimensionInfo[]) => {
      if (exportStep.type !== "missing_dimensions") return;

      // Optimistically sync the entered dimensions into estimation.measurementData
      // so the per-room metric cells reflect the new values right away. The
      // backend /pdfPreviewData endpoint persists the same merge.
      if (estimation && estimationDetail) {
        const roomNameToPageId = new Map<string, string>();
        for (const room of estimationDetail) {
          const pageId = room.dataForUI.metadata.pageId;
          const roomName = room.dataForUI.metadata.roomName;
          if (pageId && roomName) roomNameToPageId.set(roomName, pageId);
        }

        const merged = [...(estimation.measurementData ?? [])];
        for (const dim of dimensionsData) {
          const pageId = roomNameToPageId.get(dim.roomName);
          if (!pageId) continue;
          const quantity = typeof dim.quantity === "number" ? dim.quantity : Number(dim.quantity);
          if (isNaN(quantity)) continue;
          const acronym = dim.acronym.toLowerCase();
          const idx = merged.findIndex((e) => e.id === pageId);
          if (idx === -1) {
            merged.push({ id: pageId, measurement: { [acronym]: quantity } });
          } else {
            merged[idx] = {
              ...merged[idx],
              measurement: { ...merged[idx].measurement, [acronym]: quantity },
            };
          }
        }

        dispatch(
          estimationActions.setEstimationState({
            estimation: { ...estimation, measurementData: merged } as Estimation,
          }),
        );
      }

      setExportStep({
        type: "preview",
        collectedDimensions: dimensionsData,
        isTimeAndMaterial: exportStep.isTimeAndMaterial,
      });
    },
    [exportStep, estimation, estimationDetail, dispatch],
  );

  const resetExportStep = useCallback(() => {
    setExportStep({ type: "idle" });
    setIsVerbose(false);
  }, []);

  // The photo report is an interactive PDF preview in its own right — it skips the pricing/dimension
  // validation gate (irrelevant here) and opens the selection dialog directly.
  const handlePhotoReportExport = useCallback(() => {
    if (!estimateId) return;
    trackEvent(ANALYTICS_EVENTS.PDF_EXPORT_INITIATED, {
      estimate_id: estimateId,
      export_type: "photo_report",
    });
    setExportStep({ type: "photo_report" });
  }, [estimateId, trackEvent, ANALYTICS_EVENTS]);

  const generateReconFromMit = useCallback(async () => {
    if (estimation === undefined) {
      toast.error(`Attempting to generate a recon estimate from an estimate that's undefined`);
      return;
    }

    const newEstimateId = uuidv4();
    const newEstimate: Estimation = {
      id: newEstimateId,
      deleted: false,
      autoMergeRoomCompleted: false,
      dateCreated: new Date().toLocaleDateString("en-US"),
      estimateId: newEstimateId,
      projectName: `${estimation?.projectName || generateProjectName()} - RECON`,
      status: EstimateStatus.RECEIVED,
      estimateType: EstimateType.RECONSTRUCTION,
      parentEstimateId: estimateId,
      email: estimation.email,
      propertyAddress: estimation.propertyAddress,
      files: estimation.files,
      claimNumber: estimation.claimNumber,
      measurementData: estimation.measurementData,
      propertyAddressComponents: estimation.propertyAddressComponents,
      dateContacted: estimation.dateContacted,
      assignee: estimation.assignee,
      phone: estimation.phone,
      estimateReferenceNumber: estimation.estimateReferenceNumber,
      carrierId: estimation.carrierId,
      fieldData: estimation.fieldData,
      dateOfLoss: estimation.dateOfLoss,
      uploadedFilesProperties: estimation.uploadedFilesProperties,
      typeOfLoss: estimation.typeOfLoss,
      links: estimation.links,
      tpaId: estimation.tpaId,
      notes: estimation.notes,
      office: estimation.office,
      approximateClaim: estimation.approximateClaim,
      category: estimation.category,
      requesterName: estimation.requesterName,
      scopeNoteObjects: estimation.scopeNoteObjects,
      pdfPagesAsImages: estimation.pdfPagesAsImages,
      docusketchId: estimation.docusketchId,
      matterportId: estimation.matterportId,
      clusterToRoomMappings: estimation.clusterToRoomMappings,
      conversationIds: estimation.conversationIds,
      creationSource: estimation.creationSource,
    };

    const response = await estimateProvider.createEstimaticsEstimate({
      estimaticsData: newEstimate,
      scopeNoteUploadEnabled: isFeatureEnabled("SCOPE_NOTE_UPLOAD_CONFIG"),
    });
    if (!response.success) {
      toast.error("Error creating estimate");
      return;
    }

    window.open(`/estimate/${newEstimateId}`, "_blank", "noopener,noreferrer");
  }, [estimation, estimateId, isFeatureEnabled]);

  useEffect(() => {
    return () => {
      clearClipboard();
      dispatch(estimationActions.clearLineItemSelection());
      resetEstimationDetailData();
    };
  }, [resetEstimationDetailData, dispatch]);

  useEffect(() => {
    if (estimationDetail?.length) dispatch(estimationActions.setEstimationState({ uploadedFilesPropertiesCreated: [] }));
  }, [dispatch, estimationDetail?.length]);

  useEffect(() => {
    if (estimateId) {
      searchEstimateDetail(estimateId);
    }
  }, [estimateId, searchEstimateDetail]);

  const toggleShowUpdateName = useCallback(() => {
    setShowUpdateName((prev) => !prev);
  }, []);

  const backTarget = getEstimateDetailBackTarget(location.state);
  const navigateBack = useCallback(() => {
    navigate(backTarget, { replace: true });
  }, [backTarget, navigate]);

  const onBack = useCallback(() => {
    if (estimation?.projectName?.includes("New Project")) {
      toggleShowUpdateName();
      return;
    }

    clearClipboard();
    queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.all() });
    navigateBack();
  }, [estimation?.projectName, navigateBack, queryClient, toggleShowUpdateName]);

  const onExit = useCallback(() => {
    clearClipboard();
    navigateBack();
  }, [navigateBack]);

  const isMobile = useIsMobile();

  // The estimate was deleted (server returned 404). Show a terminal state instead of an
  // endless loading spinner — the refetch loop in useEstimateDetail has already stopped.
  if (estimateNotFound) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center">
        <h2 className="text-lg font-semibold text-foreground">Estimate not found</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          This estimate no longer exists. It may have been deleted.
        </p>
        <Button onClick={navigateBack}>Back to Dashboard</Button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <MobileEstimationDetail estimation={estimation} onBack={navigateBack} onAddNotePress={() => setShowAddDocDialog(true)} />
        {estimation && estimateId && (
          <AddDocumentationDialog
            open={showAddDocDialog}
            onOpenChange={setShowAddDocDialog}
            estimateId={estimateId}
            estimation={estimation}
          />
        )}
      </>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col">
      <Header
        leftSection={
          <div className="flex flex-row items-center space-x-4">
            <ArrowLeft onClick={onBack} className="cursor-pointer" />
            <label className="font-bold">{estimation?.projectName}</label>
          </div>
        }
        rightSection={
          <EstimateHeaderActions
            estimateId={estimateId!}
            estimation={estimation}
            isUpdateLineItemLoading={isUpdateLineItemLoading ?? false}
            isUpdateScopeNoteLoading={isUpdateScopeNoteLoading ?? false}
            onAddNote={() => setShowAddDocDialog(true)}
            isPdfLoading={isValidating}
            onPDFExport={handlePDFExport}
            onPhotoReportExport={handlePhotoReportExport}
            hasPdfExport={hasPdfExport}
            exportedToXactimate={exportedToXactimate ?? false}
            isExporting={isPreparingXactimateExport || isExporting}
            hasLineItems={hasLineItems}
            onExportToXactimate={handleExportToXactimate}
            onGenerateRecon={generateReconFromMit}
            onArchive={() => {
              trackEvent(ANALYTICS_EVENTS.PROJECT_ARCHIVE_INITIATED, {
                estimate_id: estimateId!,
                estimate_type: estimation?.estimateType,
              });
              setShowArchiveDialog(true);
            }}
          />
        }
      />
      <Cat3LineHighlightProvider estimateId={estimateId}>
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel className="relative" defaultSize={75} maxSize={100}>
            <RoomsSection
              estimateId={estimateId}
              lineItemDetail={lineItemDetail?.tuple}
              deleteLineItem={deleteLineItem}
              onUpload={() => setShowAddDocDialog(true)}
            />
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel defaultSize={25} minSize={25} maxSize={50}>
            <DetailsSection estimateId={estimateId} />
          </ResizablePanel>
        </ResizablePanelGroup>
      </Cat3LineHighlightProvider>
      <WarningAlert
        onCloseAlert={handleCloseAlert}
        errors={combinedAlert.errors}
        title={combinedAlert.title ?? ""}
        isOpen={!combinedAlert.isValid && !!combinedAlert.title?.length}
      />
      <FixMissingMappingModal
        onClose={onCancelMissingMapping}
        onContinue={handleContinueExporting}
        open={showMissingMappingModal ?? false}
        disableContinue={!canContinueExporting}
      />
      <FixMissingDimensionsModal
        open={exportStep.type === "missing_dimensions"}
        onClose={resetExportStep}
        onContinue={handleContinueWithDimensions}
        missingDimensions={exportStep.type === "missing_dimensions" ? exportStep.missingDimensions : []}
      />
      <PDFPreviewModal
        open={exportStep.type === "preview"}
        estimateId={estimateId!}
        projectName={estimation?.projectName}
        estimatePropertyAddress={estimation?.propertyAddress}
        jobNimbusInfo={estimation?.jobNimbusInfo}
        dimensionsData={exportStep.type === "preview" ? exportStep.collectedDimensions : []}
        isVerbose={isVerbose}
        isTimeAndMaterial={isTimeAndMaterial}
        estimateType={estimation?.estimateType}
        onComplete={() => {
          resetExportStep();
          refetchEstimation();
        }}
        onCancel={resetExportStep}
      />
      <PhotoReportExportModal
        open={exportStep.type === "photo_report"}
        estimateId={estimateId!}
        estimation={estimation}
        onComplete={() => {
          resetExportStep();
          refetchEstimation();
        }}
        onCancel={resetExportStep}
      />
      <ArchiveProjectDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        onConfirm={async () => {
          if (estimateId) {
            await archiveEstimate(estimateId);
            trackEvent(ANALYTICS_EVENTS.PROJECT_ARCHIVED, {
              estimate_id: estimateId,
              estimate_type: estimation?.estimateType,
            });
            navigate("/dashboard");
          }
        }}
        isLoading={isArchiving}
        estimateId={estimateId ?? ""}
        estimateType={estimation?.estimateType}
      />
      <ChangeDefaultNameAlert onExit={onExit} open={showUpdateName} onClose={toggleShowUpdateName} estimationId={estimateId ?? ""} />
      <ToggleShowDebugItems />
      {estimation && estimateId && (
        <AddDocumentationDialog
          open={showAddDocDialog}
          onOpenChange={setShowAddDocDialog}
          estimateId={estimateId}
          estimation={estimation}
        />
      )}
    </div>
  );
};
